<template>
  <div class="w-full">
    <div
      class="bg-white/95 backdrop-blur-sm rounded-lg sm:rounded-xl shadow-lg border border-(--p-surface-200) p-2.5 sm:p-4 mb-2 sm:mb-4 sm:sticky top-[95px] z-50"
    >
      <div
        class="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-3 sm:gap-4"
      >
        <div class="flex items-center gap-2 sm:gap-4 w-full lg:w-auto">
          <div class="flex items-center gap-2 sm:gap-3">
            <div
              class="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-(--p-primary-500) to-(--p-primary-600) rounded-lg sm:rounded-xl flex items-center justify-center shadow-md"
            >
              <i class="pi pi-table text-white !text-lg sm:!text-xl"></i>
            </div>
            <div>
              <div
                class="text-lg sm:text-xl lg:text-2xl font-bold text-(--p-anthracite-500) leading-tight"
              >
                Plans & Actions
              </div>
              <div class="text-xs sm:text-sm text-(--p-anthracite-500) font-medium">Table View</div>
            </div>
          </div>
        </div>

        <div
          class="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 w-full lg:w-auto"
        >
          <div
            class="flex items-center gap-1 sm:gap-1.5 flex-wrap w-full sm:w-auto"
            :class="isMobile ? 'flex-col items-start' : 'flex-row items-center'"
          >
            <Button
              v-if="
                user &&
                (user as { access?: number }).access !== undefined &&
                (user as { access?: number }).access !== null &&
                (user as { access?: number }).access! >= 5
              "
              icon="pi pi-plus"
              label="New Action"
              severity="primary"
              size="small"
              @click="onCreateAction"
              class="flex-1 sm:flex-none min-h-[44px] sm:min-h-0"
            />
            <Button
              v-if="!isMobile"
              icon="pi pi-chevron-down"
              label="Expand all plans"
              severity="secondary"
              size="small"
              outlined
              @click="expandAll"
              class="min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0"
            />
            <Button
              v-if="!isMobile"
              icon="pi pi-chevron-up"
              label="Collapse all plans"
              severity="secondary"
              size="small"
              outlined
              @click="collapseAll"
              class="min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0"
            />
            <ActionFilter
              v-model="filterState"
              :plans="plans"
              @display-settings-changed="onDisplaySettingsChanged"
              class="flex-1 sm:flex-none"
            />
          </div>
        </div>
      </div>
    </div>

    <DataTable
      :value="filteredPlans"
      dataKey="id"
      :paginator="true"
      :rows="10"
      :rowsPerPageOptions="[5, 10, 15, 25]"
      stripedRows
      removableSort
      v-model:expandedRows="expandedRows"
      showGridlines
      :rowHover="true"
      sortField="title"
      :sortOrder="1"
      :paginatorTemplate="
        isMobile
          ? 'RowsPerPageDropdown PrevPageLink CurrentPageReport NextPageLink'
          : 'FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown'
      "
      currentPageReportTemplate="Showing {first} to {last} of {totalRecords} plans"
      :loading="loading"
      :lazy="false"
      :virtualScrollerOptions="{
        lazy: false,
        itemSize: 100,
      }"
      class="custom-datatable responsive-datatable"
    >
      <template #empty>
        <div
          class="flex flex-col items-center justify-center py-8 sm:py-12 text-(--p-anthracite-500) px-4"
        >
          <i class="pi pi-inbox !text-3xl sm:!text-4xl mb-2 sm:mb-3 opacity-50"></i>
          <div class="text-sm sm:text-base font-medium mb-1">No plans found</div>
          <div class="text-xs sm:text-sm text-center">Try adjusting your filters</div>
        </div>
      </template>

      <Column :expander="true" headerStyle="width: 3.5rem" frozen />

      <Column
        field="title"
        header="Plan Name"
        sortable
        :style="{ minWidth: isMobile ? '200px' : '280px' }"
      >
        <template #body="{ data }">
          <div class="flex flex-col gap-1.5">
            <div class="flex items-center gap-2">
              <i class="pi pi-folder text-(--p-primary-500) text-lg"></i>
              <span class="font-semibold text-(--p-anthracite-500)">{{ data.title }}</span>
              <Badge v-if="data.active" value="Active" severity="success" size="small" />
            </div>
            <div class="flex items-center gap-2">
              <div class="flex-1 bg-(--p-surface-200) rounded-full h-1.5 overflow-hidden">
                <div
                  class="bg-gradient-to-r from-blue-500 to-green-500 h-full transition-all duration-500"
                  :style="{ width: getPlanProgress(data) + '%' }"
                ></div>
              </div>
              <span class="text-xs text-(--p-anthracite-500) font-medium min-w-[40px] text-right">
                {{ getPlanProgress(data) }}%
              </span>
            </div>
          </div>
        </template>
      </Column>

      <Column
        field="actions"
        header="Actions"
        sortable
        :style="{ minWidth: isMobile ? '80px' : '120px' }"
      >
        <template #body="{ data }">
          <div class="flex flex-col gap-1">
            <div class="flex items-center gap-2">
              <i class="pi pi-list text-(--p-anthracite-500)"></i>
              <span class="font-bold text-(--p-anthracite-500)">{{
                getFilteredActions(data.actions).length
              }}</span>
              <span class="text-xs text-(--p-anthracite-500)">of {{ data.actions.length }}</span>
            </div>
            <div class="text-xs text-(--p-anthracite-500)">
              {{ data.actions.length - getFilteredActions(data.actions).length }} filtered
            </div>
          </div>
        </template>
      </Column>

      <Column
        field="retailer"
        header="Retailers / Groups"
        sortable
        :style="{ minWidth: isMobile ? '150px' : '220px' }"
      >
        <template #body="{ data }">
          <div v-if="data.groups && data.groups.length > 0" class="flex items-start gap-2">
            <div
              class="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0"
            >
              <i class="pi pi-users text-green-700 text-sm"></i>
            </div>
            <div class="flex flex-col gap-1 min-w-0 flex-1">
              <div v-if="data.groups.length === 1" class="flex flex-col">
                <span class="font-medium text-(--p-anthracite-500) truncate">{{
                  data.groups[0].label
                }}</span>
                <span class="text-xs text-(--p-anthracite-500)">{{ data.groups[0].code }}</span>
              </div>
              <div v-else class="flex flex-col">
                <span class="font-medium text-(--p-anthracite-500)"
                  >{{ data.groups.length }} Groups</span
                >
                <div class="flex flex-wrap gap-1 mt-1">
                  <Badge
                    v-for="group in data.groups.slice(0, 3)"
                    :key="group.id"
                    :value="group.label"
                    severity="success"
                    size="small"
                  />
                  <Badge
                    v-if="data.groups.length > 3"
                    :value="`+${data.groups.length - 3}`"
                    severity="secondary"
                    size="small"
                  />
                </div>
              </div>
            </div>
          </div>
          <div
            v-else-if="data.retailers && data.retailers.length > 0"
            class="flex items-start gap-2"
          >
            <div
              class="w-8 h-8 rounded-full bg-(--p-primary-100) flex items-center justify-center flex-shrink-0"
            >
              <i class="pi pi-building text-(--p-primary-600) text-sm"></i>
            </div>
            <div class="flex flex-col gap-1 min-w-0 flex-1">
              <div v-if="data.retailers.length === 1" class="flex flex-col">
                <span class="font-medium text-(--p-anthracite-500) truncate">{{
                  data.retailers[0].label
                }}</span>
                <span class="text-xs text-(--p-anthracite-500)">{{ data.retailers[0].code }}</span>
              </div>
              <div v-else class="flex flex-col">
                <span class="font-medium text-(--p-anthracite-500)"
                  >{{ data.retailers.length }} Retailers</span
                >
                <div class="flex flex-wrap gap-1 mt-1">
                  <Badge
                    v-for="retailer in data.retailers.slice(0, 3)"
                    :key="retailer.id"
                    :value="retailer.label"
                    severity="info"
                    size="small"
                  />
                  <Badge
                    v-if="data.retailers.length > 3"
                    :value="`+${data.retailers.length - 3}`"
                    severity="secondary"
                    size="small"
                  />
                </div>
              </div>
            </div>
          </div>
          <span v-else class="text-sm text-(--p-anthracite-500) italic"
            >No Retailer or Group assigned</span
          >
        </template>
      </Column>

      <Column field="status" header="Status Overview" style="min-width: 180px">
        <template #body="{ data }">
          <div class="flex flex-col gap-2">
            <div class="flex gap-1.5">
              <div
                v-for="state in ['pending', 'active', 'complete']"
                :key="state"
                class="flex-1 flex flex-col items-center gap-1 p-1.5 rounded"
                :class="getStateBackgroundClass(state as any)"
              >
                <i :class="getStateIcon(state as any)" class="text-xs"></i>
                <span class="font-bold text-sm">{{
                  getStateCount(data.actions, state as any)
                }}</span>
              </div>
            </div>
          </div>
        </template>
      </Column>

      <template #expansion="slotProps">
        <div class="expansion-container">
          <div class="expansion-header">
            <div class="flex items-center gap-3">
              <div
                class="w-10 h-10 rounded-lg bg-(--p-primary-100) flex items-center justify-center"
              >
                <i class="pi pi-list text-(--p-primary-600)"></i>
              </div>
              <div class="flex flex-col">
                <span class="font-semibold text-(--p-anthracite-500)">
                  Actions in {{ slotProps.data.title }}
                </span>
                <span class="text-xs text-(--p-anthracite-500)">
                  {{ getFilteredActions(slotProps.data.actions).length }} of
                  {{ slotProps.data.actions.length }} actions shown
                </span>
              </div>
            </div>
            <div class="flex items-center gap-2">
              <Button
                icon="pi pi-plus"
                label="Add Action"
                severity="primary"
                size="small"
                @click="onCreateActionForPlan(slotProps.data)"
              />
              <Badge
                :value="getFilteredActions(slotProps.data.actions).length"
                severity="info"
                size="large"
              />
            </div>
          </div>

          <DataTable
            :value="getFilteredActions(slotProps.data.actions)"
            dataKey="id"
            class="nested-datatable responsive-datatable"
            @rowDblclick="onActionDblClick"
            :rowHover="true"
            stripedRows
            sortField="dueDate"
            :sortOrder="1"
            :paginator="getFilteredActions(slotProps.data.actions).length > 10"
            :rows="10"
            :rowsPerPageOptions="[5, 10, 20]"
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport"
            currentPageReportTemplate="Showing {first} to {last} of {totalRecords} actions"
          >
            <template #empty>
              <div class="flex flex-col items-center justify-center py-8 text-(--p-anthracite-500)">
                <i class="pi pi-inbox !text-3xl mb-2 opacity-50"></i>
                <div class="text-sm">No actions match the current filters</div>
              </div>
            </template>

            <Column field="title" header="Action Title" sortable style="min-width: 280px">
              <template #body="{ data }">
                <div class="flex items-center gap-2.5">
                  <div
                    class="w-1.5 h-10 rounded-full shadow-sm"
                    :style="{ backgroundColor: data.colorHex || '#60a5fa' }"
                    v-tooltip.right="'Action color indicator'"
                  ></div>
                  <div class="flex flex-col gap-0.5">
                    <div class="flex items-center gap-2">
                      <span class="font-semibold text-(--p-anthracite-500)">{{ data.title }}</span>
                      <i
                        v-if="canSeePrivate && data.isPrivate"
                        class="pi pi-lock text-(--p-anthracite-500) text-[10px]"
                        v-tooltip.top="'Private action'"
                      ></i>
                    </div>
                    <span
                      v-if="data.details"
                      class="text-xs text-(--p-anthracite-500) truncate max-w-[250px]"
                    >
                      {{ data.details.substring(0, 60) }}{{ data.details.length > 60 ? '...' : '' }}
                    </span>
                  </div>
                </div>
              </template>
            </Column>

            <Column
              v-if="displaySettings.showDueDate"
              field="dueDate"
              header="Due Date"
              sortable
              style="min-width: 150px"
            >
              <template #body="{ data }">
                <div
                  class="inline-flex items-center gap-1 px-2 py-1 rounded text-xs"
                  :class="
                    isOverdue(data)
                      ? 'bg-red-100 text-red-700 font-medium'
                      : 'bg-(--p-surface-100) text-(--p-anthracite-500)'
                  "
                >
                  <i
                    :class="isOverdue(data) ? 'pi pi-exclamation-triangle' : 'pi pi-calendar'"
                    class="text-[10px]"
                  ></i>
                  <span>{{ formatShort(data.dueDate) }}</span>
                </div>
              </template>
            </Column>

            <Column field="state" header="Status" sortable style="min-width: 120px">
              <template #body="{ data }">
                <span
                  class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
                  :class="getStateBadgeClass(data.state)"
                >
                  <i :class="getStateIcon(data.state)" class="text-[10px]"></i>
                  <span>{{ getStateLabel(data.state) }}</span>
                </span>
              </template>
            </Column>

            <Column
              v-if="displaySettings.showKpiArea"
              field="kpiArea"
              header="KPI Area"
              sortable
              style="min-width: 180px"
            >
              <template #body="{ data }">
                <div
                  v-if="data.kpiArea"
                  class="inline-flex items-center gap-1 px-2 py-1 rounded text-xs"
                  :class="getKpiAreaColorClass()"
                >
                  <i class="pi pi-chart-line text-[10px]"></i>
                  <span>{{ data.kpiArea }}</span>
                </div>
                <span v-else class="text-sm text-(--p-anthracite-500) italic">No KPI area</span>
              </template>
            </Column>

            <Column header="Actions" style="width: 60px" frozen alignFrozen="right">
              <template #body="{ data }">
                <div class="flex justify-center">
                  <Button
                    icon="pi pi-pencil"
                    severity="info"
                    size="small"
                    text
                    rounded
                    @click="onActionEdit(data)"
                    v-tooltip.left="'Edit action'"
                  />
                </div>
              </template>
            </Column>
          </DataTable>
        </div>
      </template>
    </DataTable>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import type { Plan } from '@/config/types/Plan'
import type { Action } from '@/config/types/Action'
import ActionFilter from './ActionFilter.vue'
import useActionFilters from '@/composables/ActionFilters'
import useDisplaySettings from '@/composables/useDisplaySettings'
import useActions from '@/composables/Actions'
import useAuth from '@/composables/Auth'

const props = defineProps<{ plans: Plan[] }>()
const emit = defineEmits<{
  (e: 'action:click', value: Action): void
  (e: 'action:move', payload: { action: Action; newState: 'pending' | 'active' | 'complete' }): void
  (e: 'action:create', planId?: string | number): void
}>()

const expandedRows = ref<Record<string, boolean>>({})
const loading = ref(false)
const initialLoadComplete = ref(false)

const windowWidth = ref(typeof window !== 'undefined' ? window.innerWidth : 1024)
const isMobile = computed(() => windowWidth.value < 640)

const handleResize = () => {
  if (typeof window !== 'undefined') {
    windowWidth.value = window.innerWidth
  }
}

onMounted(() => {
  if (typeof window !== 'undefined') {
    window.addEventListener('resize', handleResize)
  }
})

onUnmounted(() => {
  if (typeof window !== 'undefined') {
    window.removeEventListener('resize', handleResize)
  }
})

const filterState = ref({
  searchQuery: '',
  selectedStates: ['pending', 'active', 'complete'],
  selectedKpiAreas: [] as string[],
  selectedDueDateFilters: [] as string[],
  selectedRetailers: [] as (string | number)[],
})

const { settings: displaySettings } = useDisplaySettings()
const { isOverdue, formatShort, filterActions } = useActionFilters()
const { getStateIcon, getStateBadgeClass, getStateLabel, getKpiAreaColorClass } = useActions()
const { user } = useAuth()
const canSeePrivate = computed(() => {
  const access = (user.value as { access?: number })?.access
  return access !== undefined && access !== null && access >= 5
})

const activePlans = computed(() => {
  return props.plans || []
})

const filteredActionsCache = new Map<string, Action[]>()

const filteredPlans = computed(() => {
  if (
    !filterState.value.searchQuery &&
    filterState.value.selectedStates.length === 3 &&
    filterState.value.selectedKpiAreas.length === 0 &&
    filterState.value.selectedRetailers.length === 0
  ) {
    return activePlans.value
  }

  return activePlans.value.filter((plan) => {
    const filteredActions = getFilteredActions(plan.actions)
    return filteredActions.length > 0
  })
})

const getFilteredActions = (actions: Action[]) => {
  const actionSignature = actions
    .map((a) => `${a.id}:${a.title}:${a.state}:${a.dueDate}:${a.kpiArea || ''}`)
    .join('|')
  const cacheKey = `${actionSignature}_${JSON.stringify(filterState.value)}`

  if (filteredActionsCache.has(cacheKey)) {
    return filteredActionsCache.get(cacheKey)!
  }

  const filtered = filterActions(actions, filterState.value, props.plans)
  filteredActionsCache.set(cacheKey, filtered)

  if (filteredActionsCache.size > 100) {
    const firstKey = filteredActionsCache.keys().next().value
    if (firstKey !== undefined) {
      filteredActionsCache.delete(firstKey)
    }
  }

  return filtered
}

const planProgressCache = new Map<string | number, number>()

const getPlanProgress = (plan: Plan) => {
  if (planProgressCache.has(plan.id)) {
    return planProgressCache.get(plan.id)!
  }

  const total = plan.actions.length
  if (total === 0) {
    planProgressCache.set(plan.id, 0)
    return 0
  }

  const completed = plan.actions.filter((a) => a.state === 'complete').length
  const progress = Math.round((completed / total) * 100)
  planProgressCache.set(plan.id, progress)

  return progress
}

const getStateCount = (actions: Action[], state: 'pending' | 'active' | 'complete') => {
  return actions.filter((a) => a.state === state).length
}

const getStateBackgroundClass = (state: 'pending' | 'active' | 'complete') => {
  switch (state) {
    case 'pending':
      return 'bg-gray-50 text-(--p-anthracite-500) border border-gray-200'
    case 'active':
      return 'bg-blue-50 text-blue-700 border border-blue-200'
    case 'complete':
      return 'bg-green-50 text-green-700 border border-green-200'
  }
}

// State utilities now come from Actions composable

const onDisplaySettingsChanged = (settings: typeof displaySettings.value) => {
  displaySettings.value = { ...settings }
}

const onActionDblClick = (event: { data: Action }) => {
  const action = event.data
  emit('action:click', action)
}

const onActionEdit = (action: Action) => {
  emit('action:click', action)
}

const onCreateAction = () => {
  emit('action:create')
}

const onCreateActionForPlan = (plan: Plan) => {
  emit('action:create', plan.id)
}

const expandAll = () => {
  const expanded: Record<string, boolean> = {}
  filteredPlans.value.forEach((plan) => {
    expanded[plan.id] = true
  })
  expandedRows.value = expanded
}

const collapseAll = () => {
  expandedRows.value = {}
}

watch(
  filteredPlans,
  (newPlans) => {
    if (!initialLoadComplete.value) {
      initialLoadComplete.value = true
      expandedRows.value = {}

      setTimeout(() => {
        const expanded: Record<string, boolean> = {}
        newPlans.slice(0, 3).forEach((plan) => {
          expanded[plan.id] = true
        })
        expandedRows.value = expanded
      }, 100)
    } else {
      const currentExpanded = { ...expandedRows.value }
      expandedRows.value = currentExpanded
    }
  },
  { immediate: true },
)

watch(
  filterState,
  () => {
    filteredActionsCache.clear()
  },
  { deep: true },
)

watch(
  () => props.plans,
  () => {
    planProgressCache.clear()
    filteredActionsCache.clear()
  },
  { deep: true },
)
</script>

<style scoped>
:deep(.custom-datatable) {
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  border: 1px solid var(--p-surface-200);
}

:deep(.custom-datatable .p-datatable-table-container) {
  overflow: visible !important;
}

:deep(.custom-datatable .p-datatable-header) {
  background: var(--p-surface-50);
  border-bottom: 2px solid var(--p-surface-200);
  padding: 0.75rem;
}

@media (min-width: 640px) {
  :deep(.custom-datatable .p-datatable-header) {
    padding: 1rem;
  }
  :deep(.custom-datatable .p-datatable-thead) {
    position: sticky;
    top: 180px;
    z-index: 50;
  }
}

:deep(.nested-datatable .p-datatable-thead) {
  position: initial;
  top: 0;
  z-index: initial;
}

:deep(.custom-datatable .p-datatable-thead > tr > th) {
  background: var(--p-primary-500);
  color: white;
  font-weight: 600;
  padding: 0.625rem 0.75rem;
  border-bottom: none;
  font-size: 0.8125rem;
}

@media (min-width: 640px) {
  :deep(.custom-datatable .p-datatable-thead > tr > th) {
    padding: 0.875rem 1rem;
    font-size: 0.875rem;
  }
}



:deep(.custom-datatable .p-datatable-tbody > tr > td) {
  padding: 0.625rem 0.75rem;
  font-size: 0.8125rem;
}

@media (min-width: 640px) {
  :deep(.custom-datatable .p-datatable-tbody > tr > td) {
    padding: 0.875rem 1rem;
    font-size: 0.875rem;
  }
}

.expansion-container {
  padding: 1.5rem;
  background: linear-gradient(135deg, var(--p-surface-25) 0%, var(--p-surface-50) 100%);
  border-top: 2px solid var(--p-surface-200);
}

.expansion-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.25rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid var(--p-surface-200);
}

:deep(.nested-datatable) {
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
  border: 1px solid var(--p-surface-200);
  background: white;
}

:deep(.nested-datatable .p-datatable-thead > tr > th) {
  background: linear-gradient(180deg, var(--p-surface-100) 0%, var(--p-surface-150) 100%);
  color: var(--p-anthracite-500);
  font-weight: 600;
  font-size: 0.875rem;
  padding: 0.875rem 1rem;
  border-bottom: 2px solid var(--p-surface-200);
}

:deep(.nested-datatable .p-datatable-tbody > tr > td) {
  padding: 1rem;
  font-size: 0.875rem;
  border-bottom: 1px solid var(--p-surface-100);
}


:deep(.custom-datatable .p-paginator) {
  background: var(--p-surface-0);
  border-top: 1px solid var(--p-surface-200);
  padding: 0.5rem;
  flex-wrap: wrap;
  gap: 0.5rem;
}

@media (min-width: 640px) {
  :deep(.custom-datatable .p-paginator) {
    padding: 0.75rem 1rem;
  }
}

@media (max-width: 639px) {
  :deep(.custom-datatable .p-paginator .p-paginator-pages),
  :deep(.custom-datatable .p-paginator .p-paginator-first),
  :deep(.custom-datatable .p-paginator .p-paginator-last) {
    display: none !important;
  }

  :deep(.custom-datatable .p-paginator button) {
    min-width: 44px !important;
    min-height: 44px !important;
    padding: 0.5rem !important;
  }

  :deep(.custom-datatable .p-paginator .p-paginator-current) {
    font-size: 0.75rem !important;
    padding: 0.5rem 0.75rem !important;
    width: 100% !important;
    text-align: center !important;
    order: 999 !important;
    margin-top: 0.5rem !important;
  }
}

:deep(.custom-datatable .p-paginator .p-paginator-current) {
  color: var(--p-anthracite-500);
  font-size: 0.875rem;
}

:deep(.p-datatable-scrollable-body) {
  scrollbar-width: thin;
  scrollbar-color: var(--p-surface-300) var(--p-surface-100);
}

:deep(.p-datatable-scrollable-body::-webkit-scrollbar) {
  width: 8px;
  height: 8px;
}

:deep(.p-datatable-scrollable-body::-webkit-scrollbar-track) {
  background: var(--p-surface-100);
}

:deep(.p-datatable-scrollable-body::-webkit-scrollbar-thumb) {
  background: var(--p-surface-300);
  border-radius: 4px;
}

:deep(.p-datatable-scrollable-body::-webkit-scrollbar-thumb:hover) {
  background: var(--p-surface-400);
}

:deep(.p-datatable-row-expansion) {
  background: var(--p-surface-50) !important;
}

@media (max-width: 1024px) {
  .stats-bar {
    flex-wrap: wrap;
  }

  .stat-item {
    flex: 1 1 calc(50% - 0.5rem);
  }

  .stat-divider:nth-child(odd) {
    display: none;
  }
}

@media (max-width: 768px) {
  .stats-bar {
    gap: 0.75rem;
    padding: 0.75rem;
  }

  .stat-item {
    flex: 1 1 100%;
  }

  .stat-divider {
    display: none;
  }

  .stat-label {
    font-size: 0.6875rem;
  }

  .stat-value {
    font-size: 1rem;
  }

  .expansion-container {
    padding: 1rem;
  }
}

@media (max-width: 639px) {
  :deep(.custom-datatable) {
    border-radius: 8px;
  }

  :deep(.custom-datatable .p-datatable-table-container) {
    overflow-x: auto;
  }

  :deep(.custom-datatable .p-datatable-tbody > tr > td),
  :deep(.custom-datatable .p-datatable-thead > tr > th) {
    padding: 0.5rem 0.625rem;
    font-size: 0.75rem;
  }

  :deep(.custom-datatable .p-datatable-tbody > tr > td .flex) {
    flex-wrap: wrap;
    gap: 0.25rem;
  }

  .expansion-container {
    padding: 0.75rem;
  }
}

.dark-selector .stats-bar {
  background: var(--p-surface-800);
  border-color: var(--p-surface-700);
}

.dark-selector .stat-divider {
  background: var(--p-surface-700);
}

.dark-selector .expansion-container {
  background: linear-gradient(135deg, var(--p-surface-800) 0%, var(--p-surface-900) 100%);
  border-top-color: var(--p-surface-700);
}

.dark-selector .expansion-header {
  border-bottom-color: var(--p-surface-700);
}

.dark-selector :deep(.nested-datatable) {
  background: var(--p-surface-900);
  border-color: var(--p-surface-700);
}

.a11y-reduced-motion :deep(.custom-datatable .p-datatable-tbody > tr),
.a11y-reduced-motion :deep(.nested-datatable .p-datatable-tbody > tr),
.a11y-reduced-motion .stats-bar {
  transition: none !important;
}

.a11y-high-contrast :deep(.custom-datatable) {
  border: 2px solid var(--p-primary-500);
}
</style>
